<script lang="ts" module>
  export type ActionItem =
    | { kind: 'link'; label: string; href: string; external?: boolean }
    | {
        kind: 'button';
        label: string;
        onClick: () => void;
        destructive?: boolean;
        disabled?: boolean;
      };
</script>

<script lang="ts">
  import Icon from './Icon.svelte';

  let {
    items,
    label,
    header,
    size = 'sm',
    emphasis = 'plain',
    mobileFullWidth = false,
  }: {
    items: ActionItem[];
    label: string;
    /** Optional small header text shown at the top of the open menu —
     *  useful when the menu is visually detached from its trigger (e.g.
     *  the mobile bottom-sheet variant) and the user needs to know
     *  which entity these actions belong to. */
    header?: string;
    size?: 'sm' | 'md';
    emphasis?: 'plain' | 'filled';
    mobileFullWidth?: boolean;
  } = $props();

  let open: boolean = $state(false);
  let containerEl: HTMLDivElement | null = $state(null);

  $effect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (containerEl && !containerEl.contains(e.target as Node)) open = false;
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  });

  function onTrigger() {
    // Bubble to document so any other open ActionsMenu can close itself.
    open = !open;
  }
</script>

<div class="actions" class:mobile-full={mobileFullWidth} bind:this={containerEl}>
  <button
    type="button"
    class="trigger size-{size} emphasis-{emphasis}"
    aria-label={label}
    aria-haspopup="menu"
    aria-expanded={open}
    onclick={onTrigger}
  >
    <Icon name="more-horizontal" size={size === 'sm' ? 16 : 18} />
  </button>

  {#if open}
    <div class="menu" role="menu">
      {#if header}
        <div class="menu-header" role="presentation">{header}</div>
      {/if}
      {#each items as item}
        {#if item.kind === 'link'}
          <a
            class="item"
            href={item.href}
            target={item.external ? '_blank' : undefined}
            rel={item.external ? 'noopener noreferrer' : undefined}
            role="menuitem"
            onclick={() => (open = false)}
          >
            <span>{item.label}</span>
            {#if item.external}
              <Icon name="arrow-up-right" size={14} />
            {/if}
          </a>
        {:else}
          <button
            type="button"
            class="item"
            class:destructive={item.destructive}
            role="menuitem"
            disabled={item.disabled}
            onclick={() => {
              item.onClick();
              open = false;
            }}
          >
            <span>{item.label}</span>
          </button>
        {/if}
      {/each}
    </div>
  {/if}
</div>

<style>
  .actions { position: relative; }
  .trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 9999px;
    cursor: pointer;
    transition: background 100ms ease, color 100ms ease;
  }
  .trigger.size-sm { width: 28px; height: 28px; }
  .trigger.size-md { width: 32px; height: 32px; }
  .trigger.emphasis-plain {
    background: transparent;
    color: var(--color-muted);
  }
  .trigger.emphasis-plain:hover,
  .trigger.emphasis-plain:focus-visible,
  .trigger.emphasis-plain[aria-expanded='true'] {
    background: var(--color-surface-soft);
    color: var(--color-ink);
    outline: none;
  }
  .trigger.emphasis-filled {
    background: var(--color-surface-soft);
    color: var(--color-ink);
  }
  .trigger.emphasis-filled:hover,
  .trigger.emphasis-filled:focus-visible,
  .trigger.emphasis-filled[aria-expanded='true'] {
    background: var(--color-surface-strong);
    outline: none;
  }
  .menu {
    position: absolute;
    right: 0;
    top: calc(100% + 6px);
    z-index: 10;
    min-width: 220px;
    background: var(--color-canvas);
    border: 1px solid var(--color-hairline);
    border-radius: var(--radius-md);
    box-shadow:
      rgba(0, 0, 0, 0.02) 0 0 0 1px,
      rgba(0, 0, 0, 0.04) 0 2px 6px 0,
      rgba(0, 0, 0, 0.1) 0 4px 8px 0;
    overflow: hidden;
  }
  /* On narrow viewports for cards, anchor the menu to the bottom of the
     viewport so it doesn't get clipped by a card near the screen edge. */
  @media (max-width: 640px) {
    .mobile-full .menu {
      position: fixed;
      left: 1rem;
      right: 1rem;
      top: auto;
      bottom: 1rem;
      min-width: 0;
      z-index: 50;
    }
  }
  .menu-header {
    padding: 0.625rem 0.875rem;
    font-size: 14px;
    font-weight: 600;
    color: var(--color-ink);
    border-bottom: 1px solid var(--color-hairline-soft);
    word-break: break-word;
  }
  .item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.625rem 0.875rem;
    font-size: 14px;
    color: var(--color-ink);
    background: transparent;
    border: 0;
    width: 100%;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
    font-family: inherit;
    transition: background 100ms ease;
    min-height: 44px;
  }
  .item + .item { border-top: 1px solid var(--color-hairline-soft); }
  .item:hover,
  .item:focus-visible {
    background: var(--color-surface-soft);
    outline: none;
  }
  .item.destructive { color: var(--color-error); }
  .item.destructive:hover,
  .item.destructive:focus-visible {
    background: rgba(193, 53, 21, 0.06);
  }
  .item.destructive:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
